import { useState, useEffect, useCallback } from 'react'
import { useSession } from '@/lib/auth-client'
import { walletAPI } from '@/lib/api/wallet'

export interface CreditCard {
  id: string
  last4: string
  brand: string
  expiryMonth: number
  expiryYear: number
  isDefault: boolean
}

export interface PaymentMethods {
  cards: CreditCard[]
  defaultCard?: CreditCard
}

export function usePaymentMethods() {
  const session = useSession()
  const isAuthenticated = !!session.data?.user

  const [cards, setCards] = useState<CreditCard[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [loading, setLoading] = useState(true)

  const fetchPaymentMethods = useCallback(async () => {
    if (!isAuthenticated) {
      setCards([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const methods = await walletAPI.getPaymentMethods(true)
      setCards(
        methods.map((pm) => ({
          id: pm.id,
          last4: pm.last4,
          brand: pm.brand,
          expiryMonth: pm.expiryMonth,
          expiryYear: pm.expiryYear,
          isDefault: pm.isDefault,
        }))
      )
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load payment methods')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    void fetchPaymentMethods()
  }, [fetchPaymentMethods])

  // Listen for payment method changes (e.g. after adding a card)
  useEffect(() => {
    const handler = () => void fetchPaymentMethods()
    window.addEventListener('genie:payment-methods-changed', handler)
    return () => window.removeEventListener('genie:payment-methods-changed', handler)
  }, [fetchPaymentMethods])

  const defaultCard = cards.find((card) => card.isDefault)
  const hasCards = cards.length > 0

  const removeCard = useCallback(async (cardId: string) => {
    setIsUpdating(true)
    setError(null)
    try {
      await walletAPI.removePaymentMethod(cardId, true)
      setCards((prev) => prev.filter((c) => c.id !== cardId))
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to remove card')
      throw error
    } finally {
      setIsUpdating(false)
    }
  }, [])

  const setDefaultCard = useCallback(async (cardId: string) => {
    setIsUpdating(true)
    setError(null)
    try {
      await walletAPI.setDefaultPaymentMethod(cardId, true)
      setCards((prev) =>
        prev.map((card) => ({
          ...card,
          isDefault: card.id === cardId,
        }))
      )
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to set default card')
      throw error
    } finally {
      setIsUpdating(false)
    }
  }, [])

  const refetch = useCallback(() => {
    void fetchPaymentMethods()
  }, [fetchPaymentMethods])

  return {
    cards,
    defaultCard,
    hasCards,
    loading,
    error,
    isUpdating,
    removeCard,
    setDefaultCard,
    refetch,
  }
}
